import { createContext, useEffect, useReducer, useRef, useState, useCallback } from "react";
import axiosInstance from "../services/axios";

import { useBanner } from "./BannerContext";
import { Button } from "@chakra-ui/react";


const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  lastActivity: null,
  // Merchant switching state
  isSwitchedContext: false,
  originalMerchant: null,
  switchedAt: null,
};

export const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  refreshSession: () => Promise.resolve(),
});



const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      lastActivity: isAuthenticated ? new Date().toISOString() : null,
      // Reset context switching state on fresh login
      isSwitchedContext: false,
      originalMerchant: null,
      switchedAt: null
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
      lastActivity: new Date().toISOString(),
      // Reset context switching state on fresh login
      isSwitchedContext: false,
      originalMerchant: null,
      switchedAt: null
    };
  },

  UPDATE_ACTIVITY: (state) => {
    return {
      ...state,
      lastActivity: new Date().toISOString()
    };
  },

  VERIFY_OTP : (state) => {
    return {
      ...state
    }
  },

  SWITCH_CONTEXT: (state, action) => {
    const { contextType, contextData } = action.payload;
    return {
      ...state,
      isSwitchedContext: contextType === 'artist',
      originalMerchant: contextType === 'artist' ? state.user : null,
      switchedAt: contextType === 'artist' ? new Date().toISOString() : null,
      user: {
        ...state.user,
        currentContext: {
          type: contextType,
          ...contextData
        }
      }
    };
  },

  LOGOUT: (state) => {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      lastActivity: null,
      isSwitchedContext: false,
      originalMerchant: null,
      switchedAt: null
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [authLoading,setAuthLoading] = useState(false);
  const isMounted = useRef(false);


  const [loading, setLoading] = useState(false);

  // Activity tracking for session management
  const updateActivity = useCallback(() => {
    if (state.isAuthenticated) {
      dispatch({ type: "UPDATE_ACTIVITY" });
    }
  }, [state.isAuthenticated]);

  // Session validation function
  const sessionCache = useRef({ isValid: null, timestamp: null });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  const checkSessionValidity = async () => {
    if (!state.isAuthenticated) return true;

    const now = Date.now();
    if (sessionCache.current.timestamp && (now - sessionCache.current.timestamp < CACHE_DURATION)) {
      return sessionCache.current.isValid;
    }

    try {
      const response = await axiosInstance.get("/auth/test-token");
      const isValid = response.data ? true : false;
      sessionCache.current = { isValid, timestamp: now };
      return isValid;
    } catch (error) {
      console.error("Session validation error:", error);
      sessionCache.current = { isValid: false, timestamp: now };
      return false;
    }
  };

  // Refresh session function
  const refreshSession = async () => {
    try {
      const response = await axiosInstance.get("/users/me");
      if (response.data) {
        dispatch({
          type: "LOGIN",
          payload: {
            user: response.data
          }
        });
        updateActivity();
        return true;
      }
    } catch (error) {
      console.error("Session refresh failed:", error);
      return false;
    }
  };

  const updateUserData = (userData) => {
    dispatch({
      type: "LOGIN",
      payload: {
        user: userData
      }
    });
    updateActivity();
  };



  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    const initialize = async () => {
      // Don't try to fetch user data if we're on the login page
      if (window.location.pathname === '/login') {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        return;
      }

      try {
        const response = await axiosInstance.get("/users/me");

        if (response.data) {
          const user = response.data;

          // Check if backend returned switched context information
          if (user.account_type === 'merchant' && user.switched_context) {
            // User is in switched context, restore it
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: true,
                user: {
                  ...user,
                  currentContext: {
                    type: user.switched_context.type,
                    artist_id: user.switched_context.artist_id,
                    artist_name: user.switched_context.artist_name,
                    email: user.switched_context.email
                  }
                },
              },
            });

            // Set switched context state
            dispatch({
              type: "SWITCH_CONTEXT",
              payload: {
                contextType: user.switched_context.type,
                contextData: {
                  artist_id: user.switched_context.artist_id,
                  artist_name: user.switched_context.artist_name,
                  email: user.switched_context.email
                }
              }
            });
          } else {
            // Normal initialization
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: true,
                user: user,
              },
            });
          }
        }
      } catch (error) {
        // Any error during initialization means user is not authenticated
        console.log("User authentication failed during initialization:", error.message);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  // Activity tracking useEffect
  useEffect(() => {
    if (state.isAuthenticated) {
      const events = ['click', 'keypress', 'mousemove'];
      
      const handleActivity = () => {
        dispatch({ type: "UPDATE_ACTIVITY" });
      };

      // Add event listeners
      events.forEach(event => {
        document.addEventListener(event, handleActivity, { passive: true });
      });

      // Cleanup
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleActivity);
        });
      };
    }
  }, [state.isAuthenticated]);

  const fetchUser = async (force = false) => {
    // Don't fetch user data if we're already marked as not authenticated (unless forced)
    if (!force && state.isInitialized && !state.isAuthenticated) {
      console.log('Skipping fetchUser - already marked as not authenticated');
      return;
    }

    console.log('Fetching user data...');
    setAuthLoading(true);
    try {
      const response = await axiosInstance.get("/users/me");
      const { data: user } = response;

      dispatch({
        type: "INITIALIZE",
        payload: {
          isAuthenticated: true,
          user
        },
      });
      return user;
    } catch (error) {
      console.error('Failed to fetch user data:', error.response?.status, error.response?.data?.detail);
      dispatch({
        type: "INITIALIZE",
        payload: {
          isAuthenticated: false,
          user: null
        },
      });
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }

  const getTokens = async (userId,otp) => {
    try {
      await axiosInstance.post(`/auth/verify/otp/${userId}/${otp}`);
      updateActivity(); // Track activity on successful OTP verification
    } catch (error) {
      throw error;
    }
  };

  const login = async (userId,otp) => {
    try {
      await getTokens(userId,otp);
      const response = await axiosInstance.get("/users/me");
      const { data: user } = response;

      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });

      return user
    } catch (err) {
      return Promise.reject(err);
    }
  };

  // Method specifically for post-OAuth authentication
  const authenticateWithCookies = async () => {
    console.log('Authenticating with existing cookies...');
    setAuthLoading(true);
    try {
      const response = await axiosInstance.get("/users/me");
      const { data: user } = response;

      console.log('Successfully authenticated with cookies:', user?.email);
      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });
      return user;
    } catch (error) {
      console.error('Failed to authenticate with cookies:', error.response?.status, error.response?.data?.detail);
      dispatch({
        type: "INITIALIZE",
        payload: {
          isAuthenticated: false,
          user: null
        },
      });
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };


  const switchToArtist = async (artistId, artistData) => {
    try {
      await axiosInstance.post(`/merchants/switch-to/${artistId}`);

      dispatch({
        type: "SWITCH_CONTEXT",
        payload: {
          contextType: 'artist',
          contextData: {
            artist_id: artistId,
            artist_name: artistData.artist_name,
            email: artistData.email
          }
        }
      });

      return true;
    } catch (error) {
      console.error("Error switching to artist:", error);
      throw error;
    }
  };

  const switchBackToMerchant = async () => {
    try {
      await axiosInstance.post('/merchants/switch-back');

      dispatch({
        type: "SWITCH_CONTEXT",
        payload: {
          contextType: 'merchant',
          contextData: {}
        }
      });

      return true;
    } catch (error) {
      console.error("Error switching back to merchant:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
      dispatch({ type: "LOGOUT" });
      window.location.href = '/'; // Redirect to home page as requested
    } catch (error) {
      console.error("Logout error:", error);
      // Force logout even if API call fails
      dispatch({ type: "LOGOUT" });
      window.location.href = '/'; // Redirect to home page as requested
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        fetchUser,
        authenticateWithCookies,
        refreshSession,
        updateUserData,
        checkSessionValidity,
        updateActivity,
        authLoading,
        switchToArtist,
        switchBackToMerchant,

        // Session management
        sessionActive: state.isAuthenticated && state.lastActivity,
        lastActivity: state.lastActivity
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthContext.Consumer;

